import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import { FaUserTie, FaFilter, FaSearch, FaFileExcel, FaFileCsv, FaFilePdf, FaChevronDown, FaTimes, FaSave, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';
import SystemMessageBanner from '../components/SystemMessageBanner';
import { getFicheRowByEtatClassName } from '../utils/etatColorContrast';
import { getTodayLocal, toDateTimeLocalValue, splitDateTimeLocalValue, formatDateTimeFr } from '../utils/dateUtils';
import { toggleSortConfig, sortRowsByConfig } from '../utils/tableSort';
import './SuiviAgentsQualif.css';
import useForceDesktopViewport from '../hooks/useForceDesktopViewport';

const VALIDATED_COLOR = '#1b5e20';
const TOTAL_COLOR = '#37474f';
const TOTAL_CELL_BG = '#90a4ae';

const normalizeHex = (hex) => {
  const h = String(hex || '').replace('#', '').trim();
  if (h.length === 6) return `#${h}`;
  if (h.length === 3) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  return '#9e9e9e';
};

const darkenHex = (hex, amount = 0.18) => {
  const full = normalizeHex(hex).replace('#', '');
  const r = Math.max(0, Math.round(parseInt(full.slice(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(full.slice(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(full.slice(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const getEtatContrastColor = (bgHex) => {
  const hex = normalizeHex(bgHex).replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? '#000000' : '#ffffff';
};

const getEtatHeaderStyle = (etat) => {
  const bg = darkenHex(etat?.color || '#9e9e9e', 0.28);
  return {
    backgroundColor: bg,
    color: getEtatContrastColor(bg),
    fontWeight: 700,
    textAlign: 'center'
  };
};

function formatPersonName(nom, prenom) {
  const n = String(nom || '').trim();
  const p = String(prenom || '').trim();
  return [n, p].filter(Boolean).join(' ') || '—';
}

function formatPct(part, total) {
  const t = Number(total) || 0;
  if (t <= 0) return '0%';
  const v = Math.round((Number(part || 0) / t) * 1000) / 10;
  return `${v}%`.replace(/\.0%$/, '%');
}

function isKoEtat(etat) {
  const abbr = String(etat?.abbreviation || '').trim().toUpperCase();
  const titre = String(etat?.titre || '').trim().toUpperCase();
  return abbr === 'KO' || titre === 'KO' || Number(etat?.id) === 54;
}

function isHcEtat(etat) {
  const abbr = String(etat?.abbreviation || '').trim().toUpperCase();
  const titre = String(etat?.titre || '').trim().toUpperCase();
  if (Number(etat?.id) === 55) return true;
  if (abbr === 'HC' || titre === 'HC' || titre === 'HORS CIBLE') return true;
  if (abbr.startsWith('HHC') || titre.startsWith('HHC')) return false;
  return titre.startsWith('HORS CIBLE') || /^HC\b/.test(titre) || abbr === 'HC';
}

function getEtatCount(agentStat, etatId) {
  return agentStat?.stats?.find((s) => Number(s.id) === Number(etatId))?.count || 0;
}

function getKoHcCounts(agentStat, etats) {
  let ko = 0;
  let hc = 0;
  (etats || []).forEach((etat) => {
    const count = getEtatCount(agentStat, etat.id);
    if (isKoEtat(etat)) ko += count;
    if (isHcEtat(etat)) hc += count;
  });
  return { ko, hc };
}

/** Conformité = validé / (validé + KO + HC), hors états en écoute et autres. */
function getConformitePct(validated, agentStat, etats) {
  const { ko, hc } = getKoHcCounts(agentStat, etats);
  return formatPct(validated, Number(validated || 0) + ko + hc);
}

const getEtatCellStyle = (etat, count) => {
  const bg = normalizeHex(etat?.color || '#9e9e9e');
  return {
    backgroundColor: count > 0 ? `${bg}88` : `${bg}48`,
    color: count > 0 ? '#111' : '#555',
    fontWeight: 700,
    textAlign: 'center'
  };
};

const SuiviAgentsQualif = () => {
  useForceDesktopViewport('suivi-agents-qualif-page');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);
  const multiSelectRef = useRef(null);
  
  // État pour gérer l'édition du commentaire qualité
  const [editingComment, setEditingComment] = useState({ hash: null, value: '' });

  // Vérifier si l'utilisateur est un RE Qualification (a des agents sous sa responsabilité)
  const { data: agentsSousResponsabilite } = useQuery(
    'agents-sous-responsabilite',
    async () => {
      const res = await api.get('/management/utilisateurs');
      const agents = res.data.data?.filter(
        (u) => u.chef_equipe === user?.id && u.fonction === 3 && u.etat > 0
      ) || [];
      return agents;
    },
    { enabled: !!user }
  );

  const isREQualif = agentsSousResponsabilite && agentsSousResponsabilite.length > 0;
  
  // Vérifier si l'utilisateur est un RP Qualification (fonction 12)
  const isRPQualif = user?.fonction === 12;
  
  // Vérifier si l'utilisateur est un Administrateur (fonction 1)
  const isAdmin = user?.fonction === 1;
  const isBackoffice = Number(user?.fonction) === 11;
  const canFilterByRp = isAdmin || isBackoffice;
  
  // Vérifier si l'utilisateur est un Superviseur Qualification (fonction 2) ou RP Qualification (fonction 12)
  const isSuperviseurQualif = user?.fonction === 2;
  const canSeeCommentaireQualite = isRPQualif || isSuperviseurQualif;
  
  // Vérifier si l'utilisateur peut modifier/créer des commentaires qualité
  // Seul Admin (fonction 1) peut modifier
  const canEditCommentaireQualite = isAdmin;
  
  // Pour Superviseur Qualification (RE Qualification), le mode par défaut est 'fiches' selon les exigences
  const [viewMode, setViewMode] = useState('stats'); // 'stats' ou 'fiches'
  
  // Forcer le mode 'fiches' pour RE Qualification selon les exigences
  useEffect(() => {
    if (isREQualif && viewMode !== 'fiches') {
      setViewMode('fiches');
    }
  }, [isREQualif]);

  // États pour les filtres (date + heure)
  const [filters, setFilters] = useState({
    date_debut: getTodayLocal(),
    date_fin: getTodayLocal(),
    time_debut: '00:00',
    time_fin: '23:59',
    id_agent: '',
    id_rp: '',
    id_superviseur: '',
    id_etat_final: [] // Tableau pour multi-select
  });

  const [sortConfig, setSortConfig] = useState({ key: 'pseudo', direction: 'asc' });

  useEffect(() => {
    setSortConfig({
      key: viewMode === 'fiches' ? 'agent_pseudo' : 'pseudo',
      direction: 'asc',
    });
  }, [viewMode]);

  // Récupérer les superviseurs assignés au RP Qualification (pour filtrer leurs agents)
  const { data: superviseursAssignesRP } = useQuery(
    'superviseurs-assignes-rp',
    async () => {
      const res = await api.get('/management/utilisateurs');
      // Récupérer les superviseurs qui ont id_rp_qualif = user.id
      const superviseurs = res.data.data?.filter(u => u.id_rp_qualif === user?.id && u.etat > 0) || [];
      return superviseurs;
    },
    { enabled: isRPQualif && !!user }
  );

  // Récupérer tous les RP Qualifications (pour le filtre RP si administrateur)
  const { data: rpsData } = useQuery(
    'rps-qualif-list',
    async () => {
      const res = await api.get('/management/utilisateurs');
      return res.data.data?.filter(u => u.fonction === 12 && u.etat > 0) || [];
    },
    { enabled: canFilterByRp }
  );

  // Récupérer les superviseurs assignés à un RP spécifique (pour le filtre RP)
  const { data: superviseursDuRP } = useQuery(
    ['superviseurs-du-rp', filters.id_rp],
    async () => {
      if (!filters.id_rp) return [];
      const res = await api.get('/management/utilisateurs');
      // Récupérer les superviseurs qui ont id_rp_qualif = filters.id_rp
      return res.data.data?.filter(u => u.id_rp_qualif === parseInt(filters.id_rp) && u.etat > 0) || [];
    },
    { enabled: canFilterByRp && !!filters.id_rp }
  );

  const { data: allSuperviseursQualif } = useQuery(
    'superviseurs-qualif-suivi',
    async () => {
      const res = await api.get('/management/utilisateurs');
      const users = res.data.data || [];
      return users.filter(
        (u) =>
          Number(u.etat) > 0 &&
          users.some((a) => a.chef_equipe === u.id && Number(a.fonction) === 3 && Number(a.etat) > 0)
      );
    },
    { enabled: !isREQualif && !!user }
  );

  // Récupérer les agents qualification (filtrés par responsabilité si RE Qualification ou RP Qualification)
  const { data: agentsData } = useQuery(
    ['agents-qualif-list', isREQualif, isRPQualif, isAdmin, user?.id, superviseursAssignesRP, filters.id_rp, filters.id_superviseur, superviseursDuRP],
    async () => {
      const res = await api.get('/management/utilisateurs');
      let agents = res.data.data?.filter(u => u.fonction === 3 && u.etat > 0) || [];
      
      // Si Administrateur avec filtre RP sélectionné, filtrer par les superviseurs de ce RP
      if (canFilterByRp && filters.id_rp) {
        const superviseurIds = (superviseursDuRP || []).map((s) => String(s.id));
        agents = agents.filter((a) => superviseurIds.includes(String(a.chef_equipe)));
      }
      // Si RE Qualification, filtrer uniquement ses agents (ceux qui ont chef_equipe = user.id)
      else if (isREQualif && user?.id) {
        agents = agents.filter(a => a.chef_equipe === user.id);
      }
      // Si RP Qualification, filtrer uniquement les agents des superviseurs assignés
      else if (isRPQualif) {
        // Si les superviseurs ne sont pas encore chargés, retourner un tableau vide temporairement
        if (!superviseursAssignesRP) {
          return [];
        }
        // Si aucun superviseur n'est assigné, retourner un tableau vide
        if (superviseursAssignesRP.length === 0) {
          return [];
        }
        // Filtrer les agents dont le chef_equipe correspond à l'un des superviseurs assignés
        const superviseurIds = superviseursAssignesRP.map(s => s.id);
        agents = agents.filter(a => superviseurIds.includes(a.chef_equipe));
      }
      if (filters.id_superviseur) {
        agents = agents.filter((a) => String(a.chef_equipe) === String(filters.id_superviseur));
      }

      return agents;
    },
    { 
      // Pour RP Qualification, attendre que les superviseurs soient chargés
      // Pour Admin avec filtre RP, attendre que les superviseurs du RP soient chargés
      enabled: (!isRPQualif || (isRPQualif && superviseursAssignesRP !== undefined)) && 
               (!(canFilterByRp && filters.id_rp) || (canFilterByRp && filters.id_rp && superviseursDuRP !== undefined)),
      // Invalider quand les données de responsabilité changent
      refetchOnMount: true
    }
  );

  // Récupérer les centres
  const { data: centresData } = useQuery('centres', async () => {
    const res = await api.get('/management/centres');
    return res.data.data || [];
  });

  // Récupérer les états - uniquement groupe 0 + Validé
  const { data: etatsData } = useQuery('etats-suivi-agents-qualif', async () => {
    const res = await api.get('/management/etats');
    let etats = res.data.data || [];
    // Filtrer uniquement les états groupe 0
    etats = etats.filter(e => e.groupe === '0' || e.groupe === 0);
    return etats;
  });

  // Récupérer les statistiques
  const { data: statsData, isLoading: loadingStats } = useQuery(
    ['agents-qualif-stats', filters],
    async () => {
      const params = {};
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      if (filters.time_debut) params.time_debut = filters.time_debut;
      if (filters.time_fin) params.time_fin = filters.time_fin;
      if (filters.id_agent) params.id_agent = filters.id_agent;
      if (canFilterByRp && filters.id_rp) params.id_rp = filters.id_rp;
      if (filters.id_superviseur) params.id_superviseur = filters.id_superviseur;
      
      const res = await api.get('/statistiques/agents-qualif', { params });
      return res.data.data;
    },
    { enabled: viewMode === 'stats' }
  );

  // Récupérer les fiches créées aujourd'hui par les agents sous responsabilité
  const { data: fichesData, isLoading: loadingFiches } = useQuery(
    ['fiches-agents-sous-responsabilite', filters],
    async () => {
      const params = {
        page: 1,
        limit: 1000
      };
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      if (filters.time_debut) params.time_debut = filters.time_debut;
      if (filters.time_fin) params.time_fin = filters.time_fin;
      if (filters.id_agent) params.id_agent = filters.id_agent;
      if (canFilterByRp && filters.id_rp) params.id_rp = filters.id_rp;
      if (filters.id_superviseur) params.id_superviseur = filters.id_superviseur;
      // Envoyer tous les états au backend pour un filtrage optimisé
      if (filters.id_etat_final && Array.isArray(filters.id_etat_final) && filters.id_etat_final.length > 0) {
        params.id_etat_final = filters.id_etat_final;
      }
      
      const res = await api.get('/fiches/agents-sous-responsabilite', { params });
      return res.data;
    },
    { enabled: viewMode === 'fiches' && isREQualif }
  );

  // Filtrer les fiches par recherche rapide et par états multiples
  const filteredFiches = useMemo(() => {
    if (!fichesData?.data) return [];
    
    let filtered = fichesData.data;
    
    // Filtrer par états multiples
    if (filters.id_etat_final && Array.isArray(filters.id_etat_final) && filters.id_etat_final.length > 0) {
      filtered = filtered.filter(fiche => {
        const isGroupe0 = fiche.etat_groupe === '0' || fiche.etat_groupe === 0;
        const ficheIdEtat = String(fiche.id_etat_final);
        
        // Vérifier si "validated" est sélectionné et si la fiche est validée (hors groupe 0, hors KO)
        const isValidatedSelected = filters.id_etat_final.includes('validated');
        const isFicheKo = fiche.ko === 1 || fiche.ko === '1';
        const isFicheValidated = !isGroupe0 && !isFicheKo;
        const isKoSelected = filters.id_etat_final.includes('ko');
        
        // Vérifier si l'état de la fiche est dans la liste sélectionnée
        const isEtatSelected = filters.id_etat_final.includes(ficheIdEtat);
        
        // La fiche correspond si :
        // - "ko" est sélectionné ET la fiche est KO, OU
        // - "validated" est sélectionné ET la fiche est validée, OU
        // - l'état de la fiche est dans la liste sélectionnée
        return (isKoSelected && isFicheKo) || (isValidatedSelected && isFicheValidated) || isEtatSelected;
      });
    }
    
    // Filtrer par recherche rapide
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(fiche => {
        return (
          (fiche.nom && fiche.nom.toLowerCase().includes(term)) ||
          (fiche.prenom && fiche.prenom.toLowerCase().includes(term)) ||
          (fiche.tel && fiche.tel.includes(term)) ||
          (fiche.cp && fiche.cp.includes(term)) ||
          (fiche.agent_pseudo && fiche.agent_pseudo.toLowerCase().includes(term)) ||
          (fiche.etat_titre && fiche.etat_titre.toLowerCase().includes(term)) ||
          (fiche.commentaire_qualite && fiche.commentaire_qualite.toLowerCase().includes(term))
        );
      });
    }
    
    return filtered;
  }, [fichesData, searchTerm, filters.id_etat_final]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateTimeFilterChange = (bound, value) => {
    const { date, time } = splitDateTimeLocalValue(value);
    if (bound === 'debut') {
      setFilters((prev) => ({ ...prev, date_debut: date, time_debut: time }));
    } else {
      setFilters((prev) => ({ ...prev, date_fin: date, time_fin: time }));
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => toggleSortConfig(prev, key));
  };

  const renderSortIcon = (key) => {
    if (!key || sortConfig.key !== key) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon sort-active" />
      : <FaSortDown className="sort-icon sort-active" />;
  };

  // Mutation pour mettre à jour le commentaire qualité
  const updateCommentaireQualiteMutation = useMutation(
    async ({ hash, commentaire_qualite }) => {
      const res = await api.patch(`/fiches/${hash}/field`, {
        field: 'commentaire_qualite',
        value: commentaire_qualite
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fiches-agents-sous-responsabilite']);
        toast.success('Commentaire qualité enregistré avec succès');
        setEditingComment({ hash: null, value: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement du commentaire');
      }
    }
  );

  const handleSaveComment = (hash) => {
    const commentaire = editingComment.hash === hash ? editingComment.value : '';
    updateCommentaireQualiteMutation.mutate({ hash, commentaire_qualite: commentaire });
  };

  const handleKeyDown = (e, hash) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleSaveComment(hash);
    } else if (e.key === 'Escape') {
      setEditingComment({ hash: null, value: '' });
    }
  };

  // Fermer le dropdown multi-select quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (multiSelectRef.current && !multiSelectRef.current.contains(event.target)) {
        setIsMultiSelectOpen(false);
      }
    };

    if (isMultiSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMultiSelectOpen]);

  // Fonctions d'export
  const handleExportCSV = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      const columns = [
        { key: 'id', label: 'ID' },
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      exportToCSV(fiches, columns, 'suivi-agents-qualif-fiches');
    } else if (viewMode === 'stats' && stats.agents && stats.agents.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Agent + tous les états
      const columns = [
        { key: 'agent', label: 'Agent' },
        { key: 'nom_prenom', label: 'Nom & prénom' },
        { key: 'superviseur', label: 'Superviseur' },
      ];
      stats.etats.forEach((etat) => {
        const label = etat.abbreviation || etat.titre;
        columns.push({ key: `etat_${etat.id}`, label });
        if (isKoEtat(etat)) columns.push({ key: `pct_ko`, label: '% KO' });
        if (isHcEtat(etat)) columns.push({ key: `pct_hc`, label: '% HC' });
      });
      columns.push({ key: 'valide', label: 'Validé' });
      columns.push({ key: 'conformite', label: '% conformité' });
      columns.push({ key: 'total', label: 'Total' });
      const statsData = stats.agents.map(agentStat => {
        const total = agentStat.total || 0;
        const row = {
          agent: agentStat.agent.pseudo || 'N/A',
          nom_prenom: formatPersonName(agentStat.agent.nom, agentStat.agent.prenom),
          superviseur: agentStat.agent.superviseur_pseudo
            || formatPersonName(agentStat.agent.superviseur_nom, agentStat.agent.superviseur_prenom),
          total,
        };
        stats.etats.forEach(etat => {
          const count = getEtatCount(agentStat, etat.id);
          row[`etat_${etat.id}`] = count;
          if (isKoEtat(etat)) row.pct_ko = formatPct(count, total);
          if (isHcEtat(etat)) row.pct_hc = formatPct(count, total);
        });
        row.valide = agentStat.validated || 0;
        row.conformite = getConformitePct(agentStat.validated || 0, agentStat, stats.etats);
        return row;
      });
      exportToCSV(statsData, columns, 'suivi-agents-qualif-stats');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const handleExportExcel = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      const columns = [
        { key: 'id', label: 'ID' },
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      exportToExcel(fiches, columns, 'suivi-agents-qualif-fiches');
    } else if (viewMode === 'stats' && stats.agents && stats.agents.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Agent + tous les états
      const columns = [
        { key: 'agent', label: 'Agent' },
        { key: 'nom_prenom', label: 'Nom & prénom' },
        { key: 'superviseur', label: 'Superviseur' },
      ];
      stats.etats.forEach((etat) => {
        const label = etat.abbreviation || etat.titre;
        columns.push({ key: `etat_${etat.id}`, label });
        if (isKoEtat(etat)) columns.push({ key: `pct_ko`, label: '% KO' });
        if (isHcEtat(etat)) columns.push({ key: `pct_hc`, label: '% HC' });
      });
      columns.push({ key: 'valide', label: 'Validé' });
      columns.push({ key: 'conformite', label: '% conformité' });
      columns.push({ key: 'total', label: 'Total' });
      const statsData = stats.agents.map(agentStat => {
        const total = agentStat.total || 0;
        const row = {
          agent: agentStat.agent.pseudo || 'N/A',
          nom_prenom: formatPersonName(agentStat.agent.nom, agentStat.agent.prenom),
          superviseur: agentStat.agent.superviseur_pseudo
            || formatPersonName(agentStat.agent.superviseur_nom, agentStat.agent.superviseur_prenom),
          total,
        };
        stats.etats.forEach(etat => {
          const count = getEtatCount(agentStat, etat.id);
          row[`etat_${etat.id}`] = count;
          if (isKoEtat(etat)) row.pct_ko = formatPct(count, total);
          if (isHcEtat(etat)) row.pct_hc = formatPct(count, total);
        });
        row.valide = agentStat.validated || 0;
        row.conformite = getConformitePct(agentStat.validated || 0, agentStat, stats.etats);
        return row;
      });
      exportToExcel(statsData, columns, 'suivi-agents-qualif-stats');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const handleExportPDF = () => {
    if (viewMode === 'fiches' && fiches.length > 0) {
      const columns = [
        { key: 'id', label: 'ID' },
        { key: 'date_insert_time', label: 'Date création' },
        { key: 'agent_pseudo', label: 'Agent' },
        { key: 'nom', label: 'Nom' },
        { key: 'prenom', label: 'Prénom' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'cp', label: 'CP' },
        { key: 'etat_titre', label: 'État' },
        ...(canSeeCommentaireQualite ? [{ key: 'commentaire_qualite', label: 'Commentaire Qualité' }] : [])
      ];
      // Formater les dates pour le PDF
      const formattedData = fiches.map(fiche => ({
        ...fiche,
        date_insert_time: fiche.date_insert_time ? new Date(fiche.date_insert_time).toLocaleDateString('fr-FR') : '-'
      }));
      exportToPDF(formattedData, columns, 'suivi-agents-qualif-fiches', 'Suivi Agents Qualification - Fiches');
    } else if (viewMode === 'stats' && stats.agents && stats.agents.length > 0) {
      // Exporter les statistiques en format tableau croisé
      // Colonnes : Agent + tous les états
      const columns = [
        { key: 'agent', label: 'Agent' },
        { key: 'nom_prenom', label: 'Nom & prénom' },
        { key: 'superviseur', label: 'Superviseur' },
      ];
      stats.etats.forEach((etat) => {
        const label = etat.abbreviation || etat.titre;
        columns.push({ key: `etat_${etat.id}`, label });
        if (isKoEtat(etat)) columns.push({ key: `pct_ko`, label: '% KO' });
        if (isHcEtat(etat)) columns.push({ key: `pct_hc`, label: '% HC' });
      });
      columns.push({ key: 'valide', label: 'Validé' });
      columns.push({ key: 'conformite', label: '% conformité' });
      columns.push({ key: 'total', label: 'Total' });
      const statsData = stats.agents.map(agentStat => {
        const total = agentStat.total || 0;
        const row = {
          agent: agentStat.agent.pseudo || 'N/A',
          nom_prenom: formatPersonName(agentStat.agent.nom, agentStat.agent.prenom),
          superviseur: agentStat.agent.superviseur_pseudo
            || formatPersonName(agentStat.agent.superviseur_nom, agentStat.agent.superviseur_prenom),
          total,
        };
        stats.etats.forEach(etat => {
          const count = getEtatCount(agentStat, etat.id);
          row[`etat_${etat.id}`] = count;
          if (isKoEtat(etat)) row.pct_ko = formatPct(count, total);
          if (isHcEtat(etat)) row.pct_hc = formatPct(count, total);
        });
        row.valide = agentStat.validated || 0;
        row.conformite = getConformitePct(agentStat.validated || 0, agentStat, stats.etats);
        return row;
      });
      exportToPDF(statsData, columns, 'suivi-agents-qualif-stats', 'Suivi Agents Qualification - Statistiques');
    } else {
      alert('Aucune donnée à exporter');
    }
  };

  const byPseudo = (a, b) =>
    String(a?.pseudo || '').localeCompare(String(b?.pseudo || ''), 'fr', { sensitivity: 'base' });
  const agents = [...(agentsData || [])].sort(byPseudo);
  const superviseursOptions = (
    isRPQualif
      ? superviseursAssignesRP || []
      : filters.id_rp
        ? superviseursDuRP || []
        : allSuperviseursQualif || []
  )
    .slice()
    .sort(byPseudo);
  const rpsOptions = [...(rpsData || [])].sort(byPseudo);
  const stats = statsData || { agents: [], etats: [], period: {} };
  const fiches = filteredFiches || [];

  const sortedFiches = useMemo(
    () =>
      sortRowsByConfig(fiches, sortConfig, (row, key) => {
        if (key === 'etat') {
          if (row.ko === 1 || row.ko === '1') return 'KO';
          return row.etat_titre || '';
        }
        return row[key];
      }),
    [fiches, sortConfig]
  );

  const sortedAgentStats = useMemo(
    () =>
      sortRowsByConfig(stats.agents || [], sortConfig, (row, key) => {
        if (key === 'pseudo') return row.agent?.pseudo || '';
        if (key === 'nom_prenom') return formatPersonName(row.agent?.nom, row.agent?.prenom);
        if (key === 'superviseur') {
          return (
            row.agent?.superviseur_pseudo ||
            formatPersonName(row.agent?.superviseur_nom, row.agent?.superviseur_prenom)
          );
        }
        if (key === 'validated') return Number(row.validated || 0);
        if (key === 'total') return Number(row.total || 0);
        if (key === 'conformite') {
          const { ko, hc } = getKoHcCounts(row, stats.etats);
          const den = Number(row.validated || 0) + ko + hc;
          return den > 0 ? Number(row.validated || 0) / den : 0;
        }
        if (String(key).startsWith('etat_')) {
          return getEtatCount(row, String(key).replace('etat_', ''));
        }
        if (String(key).startsWith('pct_ko_')) {
          const etatId = String(key).replace('pct_ko_', '');
          return Number(row.total || 0) > 0
            ? getEtatCount(row, etatId) / Number(row.total)
            : 0;
        }
        if (String(key).startsWith('pct_hc_')) {
          const etatId = String(key).replace('pct_hc_', '');
          return Number(row.total || 0) > 0
            ? getEtatCount(row, etatId) / Number(row.total)
            : 0;
        }
        return '';
      }),
    [stats.agents, stats.etats, sortConfig]
  );

  return (
    <div className="suivi-agents-qualif">
      <SystemMessageBanner />
      <div className="suivi-header">
        <h1><FaUserTie /> Suivi Agents Qualification</h1>
        <div className="header-actions">
          {isREQualif && (
            <div className="view-mode-toggle">
              <button
                className={viewMode === 'stats' ? 'active' : ''}
                onClick={() => setViewMode('stats')}
              >
                Statistiques
              </button>
              <button
                className={viewMode === 'fiches' ? 'active' : ''}
                onClick={() => setViewMode('fiches')}
              >
                Fiches
              </button>
            </div>
          )}
          <button 
            className="filter-toggle-btn noprint" 
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> {showFilters ? 'Masquer' : 'Afficher'} les filtres
          </button>
          <div className="export-buttons noprint">
            <button className="export-btn" onClick={handleExportExcel} title="Exporter en Excel">
              <FaFileExcel /> Excel
            </button>
            <button className="export-btn" onClick={handleExportCSV} title="Exporter en CSV">
              <FaFileCsv /> CSV
            </button>
            <button className="export-btn" onClick={handleExportPDF} title="Exporter en PDF">
              <FaFilePdf /> PDF
            </button>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="suivi-filters">
          <div className="filter-group">
            <label>Date début (heure incluse)</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.date_debut, filters.time_debut)}
              onChange={(e) => handleDateTimeFilterChange('debut', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Date fin (heure incluse)</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.date_fin, filters.time_fin)}
              onChange={(e) => handleDateTimeFilterChange('fin', e.target.value)}
            />
          </div>
          {canFilterByRp && (
            <div className="filter-group">
              <label>RP Qualification</label>
              <select
                value={filters.id_rp}
                onChange={(e) => {
                  const nextRp = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    id_rp: nextRp,
                    id_superviseur: '',
                    id_agent: '',
                  }));
                }}
              >
                <option value="">Tous les RP</option>
                {rpsOptions.map((rp) => (
                  <option key={rp.id} value={rp.id}>{rp.pseudo || `RP #${rp.id}`}</option>
                ))}
              </select>
            </div>
          )}
          {!isREQualif && (
            <div className="filter-group">
              <label>Superviseur</label>
              <select
                value={filters.id_superviseur}
                onChange={(e) => {
                  const nextSup = e.target.value;
                  setFilters((prev) => ({ ...prev, id_superviseur: nextSup, id_agent: '' }));
                }}
              >
                <option value="">Tous les superviseurs</option>
                {superviseursOptions.map((superviseur) => (
                  <option key={superviseur.id} value={superviseur.id}>
                    {superviseur.pseudo || `ID: ${superviseur.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>Agent</label>
            <select
              value={filters.id_agent}
              onChange={(e) => handleFilterChange('id_agent', e.target.value)}
            >
              <option value="">Tous les agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.pseudo}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>État (multi-select)</label>
            <div className="multi-select-wrapper" ref={multiSelectRef}>
              <div 
                className="multi-select-trigger"
                onClick={() => setIsMultiSelectOpen(!isMultiSelectOpen)}
              >
                <div className="multi-select-selected">
                  {(!filters.id_etat_final || filters.id_etat_final.length === 0) ? (
                    <span className="multi-select-placeholder">Tous les états</span>
                  ) : (
                    <div className="multi-select-badges">
                      {(filters.id_etat_final || []).slice(0, 2).map((etatId, idx) => {
                        if (etatId === 'validated') {
                          return (
                            <span key={idx} className="multi-select-badge">
                              Validée
                            </span>
                          );
                        }
                        if (etatId === 'ko') {
                          return (
                            <span key={idx} className="multi-select-badge ko">
                              KO
                            </span>
                          );
                        }
                        const etat = etatsData?.find(e => String(e.id) === etatId);
                        return etat ? (
                          <span key={idx} className="multi-select-badge">
                            {etat.titre}
                          </span>
                        ) : null;
                      })}
                      {filters.id_etat_final && filters.id_etat_final.length > 2 && (
                        <span className="multi-select-badge more">
                          +{filters.id_etat_final.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <FaChevronDown className={`multi-select-arrow ${isMultiSelectOpen ? 'open' : ''}`} />
              </div>
              {isMultiSelectOpen && (
                <div className="multi-select-dropdown">
                  <div className="multi-select-options">
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={!filters.id_etat_final || filters.id_etat_final.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFilterChange('id_etat_final', []);
                          }
                        }}
                      />
                      <span>Tous les états</span>
                    </label>
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final && filters.id_etat_final.includes('ko')}
                        onChange={(e) => {
                          const currentEtats = filters.id_etat_final || [];
                          const newEtats = e.target.checked
                            ? [...currentEtats, 'ko']
                            : currentEtats.filter(id => id !== 'ko');
                          handleFilterChange('id_etat_final', newEtats);
                        }}
                      />
                      <span style={{ color: '#dc3545' }}>KO</span>
                    </label>
                    <label className="multi-select-option">
                      <input
                        type="checkbox"
                        checked={filters.id_etat_final && filters.id_etat_final.includes('validated')}
                        onChange={(e) => {
                          const currentEtats = filters.id_etat_final || [];
                          const newEtats = e.target.checked
                            ? [...currentEtats, 'validated']
                            : currentEtats.filter(id => id !== 'validated');
                          handleFilterChange('id_etat_final', newEtats);
                        }}
                      />
                      <span>Validée (hors groupe 0, hors KO)</span>
                    </label>
                    {etatsData?.filter(e => e.groupe === '0' || e.groupe === 0).map(etat => (
                      <label key={etat.id} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.id_etat_final && filters.id_etat_final.includes(String(etat.id))}
                          onChange={(e) => {
                            const currentEtats = filters.id_etat_final || [];
                            const newEtats = e.target.checked
                              ? [...currentEtats, String(etat.id)]
                              : currentEtats.filter(e => e !== String(etat.id));
                            handleFilterChange('id_etat_final', newEtats);
                          }}
                        />
                        <span style={{ color: etat.color || '#333' }}>{etat.titre}</span>
                      </label>
                    ))}
                  </div>
                  {filters.id_etat_final && filters.id_etat_final.length > 0 && (
                    <div className="multi-select-footer">
                      <button
                        type="button"
                        className="multi-select-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterChange('id_etat_final', []);
                        }}
                      >
                        <FaTimes /> Tout effacer
                      </button>
                      <span className="multi-select-count">
                        {filters.id_etat_final && filters.id_etat_final.length} sélectionné{(filters.id_etat_final && filters.id_etat_final.length > 1) ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recherche rapide pour les fiches */}
      {isREQualif && viewMode === 'fiches' && (
        <div className="quick-search-container">
          <FaSearch />
          <input
            type="text"
            placeholder="Recherche rapide (nom, prénom, téléphone, code postal, agent, état, commentaire qualité)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="quick-search-input"
          />
        </div>
      )}

      <div className="suivi-content">
        {viewMode === 'fiches' && isREQualif ? (
          // Vue fiches pour RE Qualification
          loadingFiches ? (
            <div className="loading">Chargement des fiches...</div>
          ) : fiches.length > 0 ? (
            <div className="fiches-table-container">
              <div className="results-info">
                {searchTerm ? (
                  <p>{fiches.length} fiche{fiches.length > 1 ? 's' : ''} trouvée{fiches.length > 1 ? 's' : ''} (sur {fichesData?.pagination?.total || 0})</p>
                ) : (
                  <p>Total: {fichesData?.pagination?.total || 0} fiche{fichesData?.pagination?.total > 1 ? 's' : ''}</p>
                )}
              </div>
              <table className="fiches-table">
                <thead>
                  <tr>
                    <th className="sortable-th" onClick={() => handleSort('id')}>ID {renderSortIcon('id')}</th>
                    <th className="sortable-th" onClick={() => handleSort('date_insert_time')}>Date création {renderSortIcon('date_insert_time')}</th>
                    <th className="sortable-th" onClick={() => handleSort('agent_pseudo')}>Agent {renderSortIcon('agent_pseudo')}</th>
                    <th className="sortable-th" onClick={() => handleSort('nom')}>Nom {renderSortIcon('nom')}</th>
                    <th className="sortable-th" onClick={() => handleSort('prenom')}>Prénom {renderSortIcon('prenom')}</th>
                    <th className="sortable-th" onClick={() => handleSort('tel')}>Téléphone {renderSortIcon('tel')}</th>
                    <th className="sortable-th" onClick={() => handleSort('cp')}>CP {renderSortIcon('cp')}</th>
                    <th className="sortable-th" onClick={() => handleSort('etat')}>État {renderSortIcon('etat')}</th>
                    {canSeeCommentaireQualite && (
                      <th className="sortable-th" onClick={() => handleSort('commentaire_qualite')}>
                        Commentaire Qualité {renderSortIcon('commentaire_qualite')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedFiches.map(fiche => {
                    const rowColor = (fiche.ko === 1 || fiche.ko === '1')
                      ? '#dc3545'
                      : (fiche.etat_groupe === '0' || fiche.etat_groupe === 0)
                        ? (fiche.etat_color || '#ccc')
                        : '#4CAF50';
                    const etatLabel = (fiche.ko === 1 || fiche.ko === '1')
                      ? 'KO'
                      : (fiche.etat_groupe === '0' || fiche.etat_groupe === 0)
                        ? (fiche.etat_titre || '')
                        : 'Validé';
                    return (
                    <tr
                      key={fiche.id}
                      className={getFicheRowByEtatClassName(rowColor)}
                      style={{ backgroundColor: rowColor }}
                    >
                      <td>{fiche.id}</td>
                      <td className="fiche-col-date">{formatDateTimeFr(fiche.date_insert_time)}</td>
                      <td>{fiche.agent_pseudo || ''}</td>
                      <td>{fiche.nom || ''}</td>
                      <td>{fiche.prenom || ''}</td>
                      <td>{fiche.tel || ''}</td>
                      <td>{fiche.cp || ''}</td>
                      <td className="etat-col-cell">
                        <span className="etat-text etat-text--wrap">{etatLabel}</span>
                      </td>
                      {canSeeCommentaireQualite && (
                        <td className="comment-qualite-cell">
                          <div className="comment-quick-edit-container">
                            {canEditCommentaireQualite ? (
                              <>
                                <div className="comment-quick-actions">
                                  {(() => {
                                    const currentValue = editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '');
                                    const originalValue = fiche.commentaire_qualite || '';
                                    const hasChanges = editingComment.hash === fiche.hash && currentValue !== originalValue;
                                    
                                    return hasChanges && (
                                      <>
                                        <button
                                          className="btn-save-comment-quick"
                                          onClick={() => handleSaveComment(fiche.hash)}
                                          disabled={updateCommentaireQualiteMutation.isLoading}
                                          title="Enregistrer (Ctrl+Enter)"
                                        >
                                          <FaSave />
                                        </button>
                                        <button
                                          className="btn-cancel-comment-quick"
                                          onClick={() => {
                                            setEditingComment({ hash: null, value: '' });
                                          }}
                                          disabled={updateCommentaireQualiteMutation.isLoading}
                                          title="Annuler (Echap)"
                                        >
                                          <FaTimes />
                                        </button>
                                      </>
                                    );
                                  })()}
                                </div>
                                <textarea
                                  value={editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '')}
                                  onChange={(e) => {
                                    if (editingComment.hash !== fiche.hash) {
                                      setEditingComment({ hash: fiche.hash, value: e.target.value });
                                    } else {
                                      setEditingComment({ ...editingComment, value: e.target.value });
                                    }
                                  }}
                                  onFocus={() => {
                                    if (editingComment.hash !== fiche.hash) {
                                      setEditingComment({ hash: fiche.hash, value: fiche.commentaire_qualite || '' });
                                    }
                                  }}
                                  onKeyDown={(e) => handleKeyDown(e, fiche.hash)}
                                  className="comment-textarea-quick"
                                  placeholder="Commentaire qualité... (Ctrl+Enter pour sauvegarder)"
                                  rows={2}
                                />
                              </>
                            ) : (
                              <div className="comment-readonly">
                                <div className="comment-readonly-text">
                                  {fiche.commentaire_qualite || <span className="no-comment">Aucun commentaire qualité</span>}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">Aucune fiche trouvée pour cette période</div>
          )
        ) : loadingStats ? (
          <div className="loading">Chargement des données...</div>
        ) : stats.agents && stats.agents.length > 0 ? (
          <>
            {stats.period && (
              <div className="period-info">
                Période :{' '}
                {stats.period.start_datetime && stats.period.end_datetime
                  ? `${formatDateTimeFr(stats.period.start_datetime)} → ${formatDateTimeFr(stats.period.end_datetime)}`
                  : `${stats.period.date_debut} ${String(filters.time_debut || '').slice(0, 5)} → ${stats.period.date_fin} ${String(filters.time_fin || '').slice(0, 5)}`}
              </div>
            )}
            <div className="table-container">
              <table className="suivi-table">
                <thead>
                  <tr>
                    <th className="agent-col-header sortable-th" onClick={() => handleSort('pseudo')}>
                      Agent {renderSortIcon('pseudo')}
                    </th>
                    <th className="name-col-header sortable-th" onClick={() => handleSort('nom_prenom')}>
                      Nom & prénom {renderSortIcon('nom_prenom')}
                    </th>
                    <th className="superviseur-col-header sortable-th" onClick={() => handleSort('superviseur')}>
                      Superviseur {renderSortIcon('superviseur')}
                    </th>
                    {stats.etats && stats.etats.length > 0 && stats.etats.map(etat => (
                      <React.Fragment key={etat.id}>
                        <th
                          className="etat-col-header sortable-th"
                          title={etat.titre}
                          style={getEtatHeaderStyle(etat)}
                          onClick={() => handleSort(`etat_${etat.id}`)}
                        >
                          {etat.abbreviation || etat.titre} {renderSortIcon(`etat_${etat.id}`)}
                        </th>
                        {isKoEtat(etat) && (
                          <th
                            className="pct-col-header sortable-th"
                            style={getEtatHeaderStyle(etat)}
                            onClick={() => handleSort(`pct_ko_${etat.id}`)}
                          >
                            % KO {renderSortIcon(`pct_ko_${etat.id}`)}
                          </th>
                        )}
                        {isHcEtat(etat) && (
                          <th
                            className="pct-col-header sortable-th"
                            style={getEtatHeaderStyle(etat)}
                            onClick={() => handleSort(`pct_hc_${etat.id}`)}
                          >
                            % HC {renderSortIcon(`pct_hc_${etat.id}`)}
                          </th>
                        )}
                      </React.Fragment>
                    ))}
                    <th
                      className="validated-col-header sortable-th"
                      style={{
                        backgroundColor: VALIDATED_COLOR,
                        color: '#ffffff',
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                      onClick={() => handleSort('validated')}
                    >
                      Validé {renderSortIcon('validated')}
                    </th>
                    <th
                      className="conformite-col-header sortable-th"
                      style={{
                        backgroundColor: VALIDATED_COLOR,
                        color: '#ffffff',
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                      onClick={() => handleSort('conformite')}
                    >
                      % conformité {renderSortIcon('conformite')}
                    </th>
                    <th
                      className="total-col-header sortable-th"
                      style={{
                        backgroundColor: TOTAL_COLOR,
                        color: '#ffffff',
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                      onClick={() => handleSort('total')}
                    >
                      Total {renderSortIcon('total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAgentStats.map((agentStat, index) => (
                    <tr key={agentStat.agent.id || index}>
                      <td className="agent-col-cell">
                        <div className="agent-cell">
                          {agentStat.agent.photo ? (
                            <img 
                              src={agentStat.agent.photo} 
                              alt={agentStat.agent.pseudo} 
                              className="agent-avatar" 
                            />
                          ) : (
                            <div className="agent-avatar placeholder">
                              {agentStat.agent.pseudo ? agentStat.agent.pseudo.charAt(0).toUpperCase() : '?'}
                            </div>
                          )}
                          <span className="agent-name">{agentStat.agent.pseudo || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="name-col-cell">
                        {formatPersonName(agentStat.agent.nom, agentStat.agent.prenom)}
                      </td>
                      <td className="superviseur-col-cell">
                        {agentStat.agent.superviseur_pseudo
                          || formatPersonName(agentStat.agent.superviseur_nom, agentStat.agent.superviseur_prenom)}
                      </td>
                      {stats.etats && stats.etats.map(etat => {
                        const count = getEtatCount(agentStat, etat.id);
                        return (
                          <React.Fragment key={etat.id}>
                            <td
                              className="etat-col-cell"
                              style={getEtatCellStyle(etat, count)}
                            >
                              {count}
                            </td>
                            {isKoEtat(etat) && (
                              <td
                                className="pct-col-cell"
                                style={getEtatCellStyle(etat, count)}
                              >
                                {formatPct(count, agentStat.total)}
                              </td>
                            )}
                            {isHcEtat(etat) && (
                              <td
                                className="pct-col-cell"
                                style={getEtatCellStyle(etat, count)}
                              >
                                {formatPct(count, agentStat.total)}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <td
                        className="validated-col-cell"
                        style={{
                          backgroundColor: (agentStat.validated || 0) > 0 ? `${VALIDATED_COLOR}88` : `${VALIDATED_COLOR}48`,
                          color: (agentStat.validated || 0) > 0 ? '#222' : '#888',
                          fontWeight: 700,
                          textAlign: 'center'
                        }}
                      >
                        {agentStat.validated || 0}
                      </td>
                      <td
                        className="conformite-col-cell"
                        style={{
                          backgroundColor: (agentStat.validated || 0) > 0 ? `${VALIDATED_COLOR}88` : `${VALIDATED_COLOR}48`,
                          color: (agentStat.validated || 0) > 0 ? '#222' : '#888',
                          fontWeight: 700,
                          textAlign: 'center'
                        }}
                      >
                        {getConformitePct(agentStat.validated || 0, agentStat, stats.etats)}
                      </td>
                      <td
                        className="total-col-cell"
                        style={{
                          backgroundColor: TOTAL_CELL_BG,
                          fontWeight: 700,
                          textAlign: 'center'
                        }}
                      >
                        {agentStat.total || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td className="agent-col-cell"><strong>Totaux</strong></td>
                    <td className="name-col-cell" />
                    <td className="superviseur-col-cell" />
                    {stats.etats && stats.etats.map(etat => {
                      const totalEtat = stats.agents.reduce((sum, agentStat) => (
                        sum + getEtatCount(agentStat, etat.id)
                      ), 0);
                      const totalFiches = stats.agents.reduce((sum, agentStat) => sum + (agentStat.total || 0), 0);
                      return (
                        <React.Fragment key={etat.id}>
                          <td
                            className="etat-col-cell totals-etat-cell"
                            style={getEtatCellStyle(etat, totalEtat)}
                          >
                            {totalEtat}
                          </td>
                          {isKoEtat(etat) && (
                            <td
                              className="pct-col-cell"
                              style={getEtatCellStyle(etat, totalEtat)}
                            >
                              {formatPct(totalEtat, totalFiches)}
                            </td>
                          )}
                          {isHcEtat(etat) && (
                            <td
                              className="pct-col-cell"
                              style={getEtatCellStyle(etat, totalEtat)}
                            >
                              {formatPct(totalEtat, totalFiches)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <td
                      className="validated-col-cell"
                      style={{
                        backgroundColor: `${VALIDATED_COLOR}88`,
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                    >
                      {stats.agents.reduce((sum, agentStat) => sum + (agentStat.validated || 0), 0)}
                    </td>
                    <td
                      className="conformite-col-cell"
                      style={{
                        backgroundColor: `${VALIDATED_COLOR}88`,
                        fontWeight: 700,
                        textAlign: 'center'
                      }}
                    >
                      {formatPct(
                        stats.agents.reduce((sum, agentStat) => sum + (agentStat.validated || 0), 0),
                        stats.agents.reduce((sum, agentStat) => {
                          const { ko, hc } = getKoHcCounts(agentStat, stats.etats);
                          return sum + (agentStat.validated || 0) + ko + hc;
                        }, 0)
                      )}
                    </td>
                    <td
                      className="total-col-cell"
                      style={{ fontWeight: 700, textAlign: 'center' }}
                    >
                      {stats.agents.reduce((sum, agentStat) => sum + (agentStat.total || 0), 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="no-data">Aucune donnée disponible pour cette période</div>
        )}
      </div>
    </div>
  );
};

export default SuiviAgentsQualif;

